import type { Entry } from 'contentful';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components';

import { isEntryOfContentType } from './guards/isEntryOfContentType';
import { isDefined } from './guards/isDefined';
import type { PageFragment } from './guards/isContentPageFragment';
import { buildDownloadUrlString } from '~/utils/buildDownloadUrlString';
import { isContentPage } from '~/utils/guards/isContentPage';
import { buildUrlString } from '~/utils/buildUrlString';
import type {
  LinkFragment,
  MetadataProviderFragment,
  NavigationItemFragment
} from '~~/lib/ContentfulGraphqlService';
import { isLinkFragment } from '~/utils/guards/isLinkFragment';
import { isNavigationItemFragment } from '~/utils/guards/isNavigationItemFragment';
import type { INavigationItem, ILink } from '~~/lib/ContentfulService';
import type { SecondaryNavigationItemSource } from '~/types/MinifiedSecondaryNavigation';
import { isMetaDataProviderFragment } from '~/utils/guards/isMetaDataProviderFragment';

/** ContentPage reference with slug only (e.g. from GlobalPageSettings). */
export type ContentPageSlugLink = {
  __typename?: 'ContentPage';
  slug?: string | null;
};

export type BuildLinkInterfaceParams =
  | null
  | Entry<unknown>
  | string
  | LinkFragment
  | INavigationItem
  | SecondaryNavigationItemSource
  | PageFragment
  | NavigationItemFragment
  | MetadataProviderFragment
  | ContentPageSlugLink;

function normalizeLinkTarget(
  target?: string | null
): HyperlinkInterface['target'] {
  if (!target || target.trim() === '') {
    return '_self';
  }
  return target as HyperlinkInterface['target'];
}

/**
 * This method creates a link to an entry.
 * A link is only created if a ContentPage (@see {@link isContentPage}) is
 * given or a link is given.
 *
 * @param element The element to use.
 * @param locale The locale to use.
 * @param withAbsolutePath If the protocol and host should be prepended.
 * @param logger The logger to use.
 */
export function buildLinkInterface(
  element?: BuildLinkInterfaceParams,
  locale = '',
  withAbsolutePath = false,
  logger = console
): HyperlinkInterface {
  if (isNavigationItemFragment(element)) {
    if (
      element.externalLink &&
      element.externalLink.externalUrl &&
      !element.externalLink.internalPage
    ) {
      return buildGraphqlLink(element.externalLink, locale);
    }
    return buildGraphqlInternalLink(element, locale, withAbsolutePath);
  }
  if (isLinkFragment(element)) {
    if (element.externalUrl && !element.internalPage) {
      return buildGraphqlLink(element, locale);
    }

    return buildGraphqlInternalLink(element, locale, withAbsolutePath);
  }

  if (isMetaDataProviderFragment(element)) {
    return {
      uri: buildUrlString(
        locale,
        element.slug ?? undefined,
        undefined,
        undefined,
        withAbsolutePath
      ),
      target: '_self',
      external: false
    };
  }

  if (isEntryOfContentType(element, 'navigationItem')) {
    return element.fields.externalLink && !element.fields.internalPage
      ? buildLinkInterface(
          element.fields.externalLink,
          locale,
          withAbsolutePath
        )
      : {
          uri: buildUrlString(
            element.sys.locale,
            element.fields.internalPage?.fields?.slug ?? '',
            undefined,
            undefined,
            withAbsolutePath
          ),
          target: normalizeLinkTarget(element.fields.linkTarget),
          external: element.fields.linkTarget === '_blank'
        };
  }

  if (isEntryOfContentType(element, 'link')) {
    if (isEntryOfContentType(element.fields.internalPage, 'download')) {
      return buildLinkInterface(
        element.fields.internalPage,
        locale,
        withAbsolutePath
      );
    }

    if (isDefined(element.fields.asset)) {
      return {
        uri: element.fields.asset?.fields?.file?.url ?? '',
        target: normalizeLinkTarget(element.fields.linkTarget),
        external: element.fields.linkTarget === '_blank'
      };
    }

    return {
      uri: linkEntryToUrl(element, locale, withAbsolutePath) ?? '',
      target: normalizeLinkTarget(element.fields.linkTarget),
      external: element.fields.linkTarget === '_blank'
    };
  }

  if (isEntryOfContentType(element, 'download')) {
    return {
      uri: buildDownloadUrlString(element.sys.locale, element.fields.slug),
      target: '_blank',
      external: true
    };
  }

  if (isContentPage(element)) {
    if (element.fields.slug != null) {
      return {
        uri: buildUrlString(
          element.sys.locale,
          element.fields.slug,
          undefined,
          undefined,
          withAbsolutePath
        ),
        target: '_self',
        external: false
      };
    }

    logger.warn(
      'buildLinkInterface',
      `referred content page ${element.sys.id} has no slug - can not build link`
    );
  }

  if (isEntryOfContentType(element, 'tag')) {
    if (element.fields?.slug) {
      return {
        uri: buildUrlString(
          element.sys.locale,
          element.fields.slug,
          undefined,
          undefined,
          withAbsolutePath
        ),
        target: '_self',
        external: false
      };
    }

    logger.warn(
      `referred Tag ${element.sys.id} has no slug - can not build link`
    );
  }

  if (typeof element === 'string') {
    return {
      uri: element,
      target: '_self',
      external: false
    };
  }

  return {
    uri: '',
    target: '_self',
    external: false
  };
}

function buildGraphqlLink(
  link: LinkFragment,
  locale: string
): HyperlinkInterface {
  let externalUrl = link?.externalUrl;
  const isShopUrl = externalUrl?.startsWith('/shop/');

  if (isShopUrl && externalUrl) {
    externalUrl = buildUrlString(locale, externalUrl);
  }

  return {
    uri: externalUrl ?? undefined,
    target: normalizeLinkTarget(link.linkTarget),
    external: link.linkTarget === '_blank'
  };
}

function buildGraphqlInternalLink(
  element: NavigationItemFragment | LinkFragment,
  locale: string,
  withAbsolutePath: boolean
): HyperlinkInterface {
  const link = element.internalPage
    ? element
    : isNavigationItemFragment(element)
      ? element.externalLink
      : null;

  if (link?.internalPage?.__typename === 'Download') {
    return {
      uri: buildDownloadUrlString(locale, link?.internalPage.slug ?? ''),
      target: '_blank',
      external: true
    };
  }

  const slug = link?.internalPage?.slug ?? '';
  const isLink = isLinkFragment(link);
  let query = (isLink && link.queryParameter) || undefined;
  let anchor = (isLink && link.internalAnchor?.id) || undefined;

  if (
    anchor &&
    isLink &&
    link.internalAnchor?.__typename === 'MediaTabItem' &&
    link.internalAnchor?.sys.id
  ) {
    if (query) {
      query += '&';
    } else {
      query = '';
    }
    const id = link.internalAnchor?.id ?? link.internalAnchor?.sys.id;

    query += `_aTabs=${id}`;
    anchor = `tab-panel-${id}`;
  }

  const uri = buildUrlString(locale, slug, query, anchor, withAbsolutePath);

  return {
    uri,
    target: normalizeLinkTarget(link?.linkTarget),
    external: link?.linkTarget === '_blank'
  };
}

function linkEntryToUrl(
  link: ILink,
  locale: string,
  withAbsolutePath = false
): string | undefined {
  const pageOrDownload = link?.fields.internalPage;

  if (!isDefined(pageOrDownload)) {
    const isShopUrl = link?.fields.externalUrl?.startsWith('/shop/');

    if (isShopUrl) {
      return buildUrlString(locale, link?.fields.externalUrl);
    }

    if (link.fields.documentId) {
      return `#download=${link.fields.documentId}`;
    }

    if (link.fields.softwareId) {
      return `#software=${link.fields.softwareId}`;
    }

    return link?.fields.externalUrl;
  }

  if (isEntryOfContentType(pageOrDownload, 'download')) {
    return buildDownloadUrlString(locale, pageOrDownload.fields.slug);
  }

  const slug = pageOrDownload.fields?.slug;

  if (!isDefined(slug)) {
    return;
  }

  const anchorFields = link?.fields.internalAnchor?.fields;
  let query = link?.fields?.queryParameter;
  let anchor =
    anchorFields && 'id' in anchorFields ? anchorFields.id : undefined;

  if (
    anchor &&
    link.fields.internalAnchor?.sys?.contentType?.sys?.id === 'mediaTabItem' &&
    link.fields.internalAnchor?.sys.id
  ) {
    if (query) {
      query += '&';
    } else {
      query = '';
    }
    const id =
      link.fields.internalAnchor?.fields?.id ??
      link.fields.internalAnchor?.sys.id;

    query += `_aTabs=${id}`;
    anchor = `tab-panel-${id}`;
  }

  return buildUrlString(locale, slug, query, anchor, withAbsolutePath);
}
